import { Plan } from "@/features/planner/api/planner";

interface ConnectivityGuideProps {
    plan: Plan;
}

const safeParse = (content: any) => {
    if (!content) return null;
    if (typeof content !== 'string') return content;

    try {
        return JSON.parse(content);
    } catch (e) {
        // 1. Try simple python fixes
        let fixed = content
            .replace(/\bNone\b/g, 'null')
            .replace(/\bTrue\b/g, 'true')
            .replace(/\bFalse\b/g, 'false');

        try {
            // 2. Try cleaning quotes for JSON.parse
            let jsonString = fixed
                .replace(/(['"])?([a-zA-Z0-9_]+)(['"])?\s*:/g, '"$2": ') // Ensure numeric/words keys are double quoted
                .replace(/'/g, '"'); // Naive replace all single quotes
            return JSON.parse(jsonString);
        } catch (e2) {
            // 3. Fallback: Regex extraction for flat dictionaries (common in this app)
            // This handles mixed quotes like {'key': "Value with 'single' quotes"} better
            const extracted: Record<string, string> = {};
            let hasMatches = false;

            // Match key: 'value' or key: "value"
            // Regex explanation:
            // (['"]?)([a-zA-Z0-9_]+)\1\s*:\s*   -> Key (optionally quoted)
            // (['"])                            -> Value start quote (group 3)
            // ([\s\S]*?)                        -> Value content (non-greedy) (group 4)
            // \3                                -> Matching value end quote
            const regex = /(['"]?)([a-zA-Z0-9_]+)\1\s*:\s*(['"])([\s\S]*?)\3/g;

            let match;
            while ((match = regex.exec(fixed)) !== null) {
                hasMatches = true;
                const key = match[2];
                const value = match[4];
                extracted[key] = value;
            }

            if (hasMatches) return extracted;

            return content;
        }
    }
};

const RenderContent = ({ content, type }: { content: any, type?: 'sim' | 'apps' | 'offline' | 'language' | 'cost' }) => {
    if (!content) return null;
    const data = safeParse(content);

    if (!data) return null;

    if (typeof data === 'string') {
        return <span className="text-[#333]">{data}</span>;
    }

    if (Array.isArray(data)) {
        return (
            <div className="flex flex-wrap gap-2">
                {data.map((item, i) => (
                    <span key={i} className="bg-white border border-[#BFDBFE] text-[#1E3A8A] px-3 py-2 rounded-lg text-[14px] font-medium shadow-sm flex items-center gap-2">
                        <span>📱</span> {String(item)}
                    </span>
                ))}
            </div>
        );
    }

    // Cost Rendering
    if (type === 'cost' && typeof data === 'object') {
        // e.g. {'local_currency': '500-700 BDT', 'EUR_equivalent': '5-7 EUR'}
        return (
            <div className="flex flex-wrap gap-2">
                {Object.entries(data).map(([key, value]) => (
                    <span key={key} className="bg-[#E0F2F1] text-[#00695C] border border-[#B2DFDB] px-2 py-1 rounded text-[12px] font-medium">
                        {String(value)}
                    </span>
                ))}
            </div>
        );
    }

    // Specific Rendering for SIM
    if (type === 'sim' && typeof data === 'object') {
        const { best_operator, typical_tourist_data_plan, where_to_buy, cost } = data;
        return (
            <div className="space-y-3">
                {(best_operator || typical_tourist_data_plan) && (
                    <div className="text-[15px] text-[#222]">
                        <div className="font-semibold mb-1 text-[#187A85]">Recommendation</div>
                        <div>
                            Get a <span className="font-bold">{best_operator || "local"}</span> SIM card
                            {typical_tourist_data_plan ? ` with ${typical_tourist_data_plan}` : ""}.
                        </div>
                    </div>
                )}
                {where_to_buy && (
                    <div className="text-[14px] text-[#444] bg-white p-3 rounded-lg border border-[#EAEAEA]">
                        <div className="flex items-start gap-2">
                            <span className="text-[16px] shrink-0">📍</span>
                            <span>{where_to_buy}</span>
                        </div>
                    </div>
                )}
                {/* Legacy cost field inside sim object support */}
                {cost && (
                    <div className="flex items-start gap-2 text-[14px] text-[#444]">
                        <span className="text-[16px] shrink-0">💰</span>
                        <span>Estimated cost: {cost}</span>
                    </div>
                )}
            </div>
        );
    }

    // Specific Rendering for Offline
    if (type === 'offline' && typeof data === 'object') {
        // Mapping internal keys to nicer labels
        const labels: Record<string, string> = {
            download_offline_maps: "🗺️ Maps",
            translation_phrases: "🗣️ Phrases",
            offline_apps: "📲 Apps"
        };

        return (
            <div className="space-y-3">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="text-[14px] text-[#333]">
                        <div className="font-bold text-[#E76828] mb-1 text-[13px] uppercase tracking-wide">
                            {labels[key] || key.replace(/_/g, ' ')}
                        </div>
                        <div className="pl-4 border-l-2 border-[#FFE0B2]">
                            {String(value)}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    // Language Barrier
    if (type === 'language' && typeof data === 'object') {
        return (
            <div className="space-y-4">
                {data.assessment && (
                    <div className="text-[15px] text-[#222] italic">
                        "{data.assessment}"
                    </div>
                )}
                {data.practical_tips && (
                    <div className="bg-white p-3 rounded-lg border border-[#E1BEE7]">
                        <div className="text-[12px] font-bold text-[#6A1B9A] uppercase mb-1">Practical Tips</div>
                        <div className="text-[14px] text-[#4A148C]">
                            {data.practical_tips}
                        </div>
                    </div>
                )}
                {/* Fallback for other keys */}
                {Object.entries(data).map(([key, value]) => {
                    if (key === 'assessment' || key === 'practical_tips') return null;
                    return (
                        <div key={key} className="text-[14px]">
                            <span className="font-bold capitalize">{key.replace(/_/g, ' ')}:</span> {String(value)}
                        </div>
                    );
                })}
            </div>
        );
    }

    // Generic Object Render
    if (typeof data === 'object') {
        return (
            <div className="space-y-2">
                {Object.entries(data).map(([key, value]) => (
                    <div key={key} className="text-[14px] text-[#333]">
                        <span className="font-bold capitalize">{key.replace(/_/g, ' ')}:</span> {String(value)}
                    </div>
                ))}
            </div>
        );
    }

    return <span>{String(data)}</span>;
};


export const ConnectivityGuide = ({ plan }: ConnectivityGuideProps) => {
    if (!plan || !plan.connectivity) return null;

    const { connectivity, local_intelligence: intelligence } = plan;

    return (
        <div className="mx-5 p-10 bg-white">
            <h2 className="text-center text-[28px] font-bold text-[#187A85] mb-6 font-serif">
                Connectivity Guide
            </h2>

            <div className="flex flex-col gap-4">
                {/* SIM Card */}
                <div className="bg-[#FFF9F5] rounded-xl p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[24px]">📶</span>
                            <div className="text-[18px] font-bold text-[#187A85] uppercase tracking-wide">
                                SIM Card
                            </div>
                        </div>
                        {connectivity.estimated_cost && (
                            <RenderContent content={connectivity.estimated_cost} type="cost" />
                        )}
                    </div>
                    <div className="pl-[5px] md:pl-[34px]">
                        <RenderContent content={connectivity.sim_recommendation} type="sim" />

                        {!safeParse(connectivity.sim_recommendation) && (
                            <div className="text-[14px] text-[#333333]">
                                Recommended carriers: <strong>{intelligence?.mobile_operators?.join(", ") || "Local networks"}</strong>.
                            </div>
                        )}
                    </div>
                </div>

                {/* Essential Apps */}
                <div className="bg-[#EFF6FF] rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-[24px]">📲</span>
                        <div className="text-[18px] font-bold text-[#1E40AF] uppercase tracking-wide">
                            Essential Apps
                        </div>
                    </div>
                    <div className="pl-[5px] md:pl-[34px]">
                        <RenderContent content={connectivity.essential_apps} type="apps" />
                        {!safeParse(connectivity.essential_apps) && (
                            <div className="text-[14px] text-[#333] mt-2">
                                Navigation (Google Maps), Transport (Uber/Bolt), Translation (Google Translate).
                            </div>
                        )}
                    </div>
                </div>

                {/* Before You Go Offline */}
                <div className="bg-[#FFF9F5] rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-[24px]">📴</span>
                        <div className="text-[18px] font-bold text-[#E76828] uppercase tracking-wide">
                            Before You Go Offline
                        </div>
                    </div>
                    <div className="pl-[5px] md:pl-[34px]">
                        <RenderContent content={connectivity.offline_tips} type="offline" />
                    </div>
                </div>

                {/* WiFi */}
                <div className="bg-[#E0F2F1] rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-[24px]">🌐</span>
                        <div className="text-[18px] font-bold text-[#00695C] uppercase tracking-wide">
                            WiFi
                        </div>
                    </div>
                    <div className="pl-[5px] md:pl-[34px] text-[14px] text-[#004D40] leading-relaxed">
                        <RenderContent content={connectivity.wifi_availability} />
                    </div>
                </div>

                {/* Language */}
                <div className="bg-[#F3E5F5] rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-3">
                        <span className="text-[24px]">🗣️</span>
                        <div className="text-[18px] font-bold text-[#6A1B9A] uppercase tracking-wide">
                            Language
                        </div>
                    </div>
                    <div className="pl-[5px] md:pl-[34px] text-[14px] text-[#4A148C] leading-relaxed">
                        <RenderContent content={connectivity.language_barrier} type="language" />

                        {!safeParse(connectivity.language_barrier) && (
                            <div className="mt-2">
                                Official: <strong>{intelligence?.official_languages?.map(l => l.name).join(", ") || "Local Language"}</strong>.
                                English is likely spoken in tourist areas.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
